import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import posthog from 'posthog-js'
import { onGetDashboardIndicatorsAsync } from './Dashboard.slice'
import { onGetPensionHistoryAsync } from '../PensionHistory/PensionHistory.slice'
import { getPensionConfig } from '../OnboardingPension/OnboardingPension.slice'
import DashboardHeroWithPension from './DashboardHeroWithPension.container'
import DashboardHeroWithoutPension from './DashboardHeroWithoutPension.container'
import Spinner from '../../components/Spinners/Spinner'
import Card from '../../components/Cards/Card'
import MessageNotice from '../../components/MessageNotice/MessageNotice'
import GeneratingPensionOverlay from '../../components/Pension/GeneratingPensionOverlay'
import { default as Button } from '../../components/Buttons/BaseButton'
import useEnsurePensionCalculation from '../../hooks/useEnsurePensionCalculation'
import { getLogger } from '../../core/debuggers'
import { mapDashboardToCards } from '../../mappers/dashboard.adapter'
import { formatCurrency, formatFriendlyDate, formatMonth, isFutureMonthKey } from '../../config/resources/utils'
import {
  REACT_APP_FEATURE_FLAG_INDICATORS_SECTION,
  isFeatureEnabled,
} from '../../config/settings/environment'
import routes from '../../config/settings/routes'
import {
  METHOD,
  normalizePensionMethod,
} from '../../config/settings/constants'
import {
  getLatestPensionCalculation,
  updatePensionCalculationStatusAsync,
} from '../../services/pension'
import { PENSION_HERO_ID } from './pensionHeroAnchors'
import * as S from './Dashboard.styles'

const showIndicatorsSection = isFeatureEnabled(
  REACT_APP_FEATURE_FLAG_INDICATORS_SECTION
)

const logger = getLogger('Dashboard.container.jsx')

const SETUP_BANNER_DISMISS_KEY = 'pension_setup_banner_dismissed'
const AMOUNT_MATCH_STORAGE_PREFIX = 'cotidia_amount_match_'
const AMOUNT_MATCH_SHOWN_PREFIX = 'cotidia_amount_match_shown_'

const amountMatchStorageKey = (userId, periodKey) =>
  `${AMOUNT_MATCH_STORAGE_PREFIX}${userId}_${periodKey}`

const amountMatchShownKey = (userId, periodKey) =>
  `${AMOUNT_MATCH_SHOWN_PREFIX}${userId}_${periodKey}`

const readAmountMatchAnswer = (userId, periodKey) => {
  if (!userId || !periodKey || typeof window === 'undefined') return null
  try {
    const value = window.localStorage.getItem(
      amountMatchStorageKey(userId, periodKey)
    )
    return value === 'yes' || value === 'no' ? value : null
  } catch {
    return null
  }
}

const writeAmountMatchAnswer = (userId, periodKey, answer) => {
  if (!userId || !periodKey || typeof window === 'undefined') return
  try {
    window.localStorage.setItem(
      amountMatchStorageKey(userId, periodKey),
      answer
    )
  } catch {
    // ignore quota / private mode
  }
}

const calculationFootnote = (method) => {
  const normalized = method ? normalizePensionMethod(method) : null
  if (normalized === METHOD.IMM) {
    return 'Con el sueldo mínimo (IMM) vigente y tu porcentaje de pensión.'
  }
  if (normalized === METHOD.FIXED_AMOUNT) {
    return 'Según el monto fijo configurado en tu pensión.'
  }
  // UTM (default) and unknown
  return 'Con la UTM oficial del SII y tu cantidad de UTM.'
}

const hasFiniteNumber = (value) =>
  value != null && value !== '' && Number.isFinite(Number(value))

const monthInSentence = (value) => {
  const month = formatMonth(value)
  if (!month) return ''
  return month.charAt(0).toLowerCase() + month.slice(1)
}

const isPaidStatus = (status) => status === 'payed'

/**
 * ahead: calc month is in the future → calm "al día", no pay CTA
 * paid: status payed → calm "al día" + undo
 * due: unpaid for current or past month → register payment
 */
const resolveHeroMode = (latestCalc) => {
  if (!latestCalc) return 'due'
  if (isPaidStatus(latestCalc.status)) return 'paid'
  if (isFutureMonthKey(latestCalc.targetMonthKey)) return 'ahead'
  return 'due'
}

const Dashboard = () => {
  const { error, loading, dashboard = {} } = useSelector(state => state.dashboard)
  const { config, fetchStatus: configFetchStatus } = useSelector(
    state => state.adminPensionConfig
  )
  const userId = useSelector(state => state.adminAuth.user?.id)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const [forceGenerate] = useState(() => Boolean(location.state?.generatePension))
  const [bannerDismissed, setBannerDismissed] = useState(
    () => sessionStorage.getItem(SETUP_BANNER_DISMISS_KEY) === 'true'
  )
  const [latestCalc, setLatestCalc] = useState(null)
  const [paymentUpdating, setPaymentUpdating] = useState(false)
  const [paymentError, setPaymentError] = useState(null)
  const [amountMatchAnswer, setAmountMatchAnswer] = useState(null)
  const [amountMatchThanks, setAmountMatchThanks] = useState(false)
  const [amountMatchFollowUp, setAmountMatchFollowUp] = useState(null)

  logger.debug('dashboard', dashboard)
  const cardsData = showIndicatorsSection ? mapDashboardToCards(dashboard) : []

  useEffect(() => {
    dispatch(onGetDashboardIndicatorsAsync())
    dispatch(getPensionConfig())
  }, [dispatch])

  useEffect(() => {
    if (location.state?.generatePension) {
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.pathname, location.state, navigate])

  const configReady =
    configFetchStatus === 'succeeded' || configFetchStatus === 'failed'
  const needsSetup = configReady && !config
  const hasPensionValue = hasFiniteNumber(dashboard.lastPensionValue)
  const showSetupBanner = needsSetup && !bannerDismissed

  const refreshDashboard = useCallback(() => {
    dispatch(onGetDashboardIndicatorsAsync())
    dispatch(onGetPensionHistoryAsync())
  }, [dispatch])

  const loadLatestCalc = useCallback(async () => {
    if (!userId) {
      setLatestCalc(null)
      return
    }

    try {
      const calc = await getLatestPensionCalculation(userId)
      setLatestCalc(calc)
      setPaymentError(null)
    } catch (err) {
      logger.error('Failed to load latest pension calculation', err)
    }
  }, [userId])

  useEffect(() => {
    if (!loading && hasPensionValue) {
      loadLatestCalc()
    }
  }, [loading, hasPensionValue, loadLatestCalc])

  const amountMatchPeriodKey =
    latestCalc?.targetMonthKey ||
    latestCalc?.targetMonth ||
    dashboard.lastPensionTargetMonth ||
    null

  useEffect(() => {
    setAmountMatchThanks(false)
    setAmountMatchFollowUp(null)
    setAmountMatchAnswer(readAmountMatchAnswer(userId, amountMatchPeriodKey))
  }, [userId, amountMatchPeriodKey])

  const {
    generating,
    error: generationError,
    retry: retryGeneration,
  } = useEnsurePensionCalculation({
    userId,
    hasConfig: Boolean(config),
    configReady,
    force: forceGenerate,
    config,
    configMethod: config?.method || null,
    enabled: true,
    onCompleted: () => {
      refreshDashboard()
      loadLatestCalc()
    },
  })

  const pensionDiff = Number(dashboard.pensionDiff) || 0
  const hasPositiveDiff = pensionDiff > 0
  const hasNeutralDiff = pensionDiff === 0
  const hasNegativeDiff = pensionDiff < 0
  const hasPreviousValue = hasFiniteNumber(dashboard.previousPensionValue)
  const pensionMonth = monthInSentence(
    latestCalc?.targetMonthRaw || dashboard.lastPensionTargetMonth
  )
  const isPaid = isPaidStatus(latestCalc?.status)
  const heroMode = resolveHeroMode(latestCalc)

  const trendLabel = hasNeutralDiff
    ? 'Se mantiene igual que el mes anterior'
    : hasPositiveDiff
      ? `Subió ${formatCurrency(pensionDiff)} respecto al mes anterior`
      : `Bajó ${formatCurrency(Math.abs(pensionDiff))} respecto al mes anterior`

  const goToPreferences = () => {
    navigate(`${routes.preferences}#metodo-de-calculo`)
  }

  const dismissSetupBanner = () => {
    sessionStorage.setItem(SETUP_BANNER_DISMISS_KEY, 'true')
    setBannerDismissed(true)
  }

  const amountMatchState = (() => {
    if (!hasPensionValue || !amountMatchPeriodKey) return null
    if (heroMode !== 'paid') return null
    if (amountMatchThanks) return 'thanks'
    if (amountMatchAnswer) return null
    return 'ask'
  })()

  useEffect(() => {
    if (amountMatchState !== 'ask' || !userId || !amountMatchPeriodKey) return
    const key = amountMatchShownKey(userId, amountMatchPeriodKey)
    try {
      if (window.localStorage.getItem(key) === '1') return
      window.localStorage.setItem(key, '1')
    } catch {
      return
    }
    posthog.capture('pension_amount_match_shown', {
      source: 'dashboard',
      period: amountMatchPeriodKey,
    })
  }, [amountMatchState, userId, amountMatchPeriodKey])

  const handleAmountMatch = (matched) => {
    if (!userId || !amountMatchPeriodKey) return

    const answer = matched ? 'yes' : 'no'
    writeAmountMatchAnswer(userId, amountMatchPeriodKey, answer)

    posthog.capture('pension_amount_matched', {
      matched,
      source: 'dashboard',
      period: amountMatchPeriodKey,
    })

    setAmountMatchThanks(true)
    setAmountMatchAnswer(answer)
    setAmountMatchFollowUp(answer)
  }

  const handleTogglePayment = async () => {
    if (!latestCalc?.id || paymentUpdating) return
    if (resolveHeroMode(latestCalc) === 'ahead') return

    const nextStatus = isPaid ? 'pending' : 'payed'
    const paymentAt = nextStatus === 'payed' ? new Date() : null

    setPaymentUpdating(true)
    setPaymentError(null)

    try {
      const paymentEventProps = {
        source: 'dashboard',
        period: latestCalc.targetMonth,
        amount:
          latestCalc.amount != null ? Number(latestCalc.amount) : null,
      }
      if (nextStatus === 'payed') {
        posthog.capture('pension_payment_registered', paymentEventProps)
      } else {
        posthog.capture('pension_payment_undone', paymentEventProps)
      }

      const updated = await updatePensionCalculationStatusAsync({
        id: latestCalc.id,
        status: nextStatus,
        paymentAt,
      })

      setLatestCalc((prev) => ({
        ...prev,
        status: updated.status,
        paymentAt: formatFriendlyDate(updated.payment_at),
        targetMonth: prev?.targetMonth,
        targetMonthRaw: prev?.targetMonthRaw,
        targetMonthKey: prev?.targetMonthKey,
      }))
    } catch (err) {
      logger.error('Failed to update payment status from dashboard', err)
      setPaymentError('No pudimos actualizar el estado del pago. Intenta de nuevo.')
    } finally {
      setPaymentUpdating(false)
    }
  }

  const dashboardHeroProps = {
    pensionMonth,
    dashboard,
    isPaid,
    heroMode,
    hasPreviousValue,
    hasNeutralDiff,
    hasNegativeDiff,
    hasPositiveDiff,
    trendLabel,
    latestCalc,
    paymentUpdating,
    handleTogglePayment,
    paymentError,
    calculationFootnote,
    config,
    needsSetup,
    goToPreferences,
    amountMatchState,
    amountMatchFollowUp,
    onAmountMatchYes: () => handleAmountMatch(true),
    onAmountMatchNo: () => handleAmountMatch(false),
    onReviewFactor: goToPreferences,
  }

  return (
    <S.Section>
      {generating && (
        <S.GeneratingWrap>
          <GeneratingPensionOverlay />
        </S.GeneratingWrap>
      )}

      {!generating && generationError && (
        <MessageNotice type="error">
          <S.SetupBanner>
            <div>
              <S.SetupBannerTitle>No pudimos preparar tu monto</S.SetupBannerTitle>
              <S.SetupBannerText>{generationError}</S.SetupBannerText>
            </div>
            <S.SetupBannerActions>
              <Button type="button" onClick={retryGeneration} width="auto">
                Reintentar
              </Button>
            </S.SetupBannerActions>
          </S.SetupBanner>
        </MessageNotice>
      )}

      {loading && !generating && <Spinner />}
      {!loading && !generating && error && (
        <S.ErrorMessage>No pudimos cargar tus indicadores. Intenta nuevamente.</S.ErrorMessage>
      )}
      {!loading && !error && !generating && (
        <>
          {showSetupBanner && (
            <MessageNotice type="warning">
              <S.SetupBanner>
                <div>
                  <S.SetupBannerTitle>
                    Falta un dato para mostrarte el monto
                  </S.SetupBannerTitle>
                  <S.SetupBannerText>
                    Guarda tu método y valor en Preferencias. Cuando lo
                    completes, aquí verás tu pensión con claridad.
                  </S.SetupBannerText>
                </div>
                <S.SetupBannerActions>
                  <Button type="button" onClick={goToPreferences} width="auto">
                    Ir a Preferencias
                  </Button>
                  <S.SetupBannerLink type="button" onClick={dismissSetupBanner}>
                    Ahora no
                  </S.SetupBannerLink>
                </S.SetupBannerActions>
              </S.SetupBanner>
            </MessageNotice>
          )}

          <S.HeroCard id={PENSION_HERO_ID}>
            {hasPensionValue ? (
              <DashboardHeroWithPension dashboardHeroProps={dashboardHeroProps} />
            ) : (
              <DashboardHeroWithoutPension
                dashboardHeroProps={dashboardHeroProps}
              />
            )}
          </S.HeroCard>

          {showIndicatorsSection && (
            <S.CardsWrapper>
              {cardsData.map((card) => (
                <Card key={card.key || card.label}>
                  <S.StatContent>
                    <S.StatValue>{card.value}</S.StatValue>
                    <S.StatLabel>{card.label}</S.StatLabel>
                  </S.StatContent>
                  <S.StatIcon>
                    <span className={card.icon} />
                  </S.StatIcon>
                </Card>
              ))}
            </S.CardsWrapper>
          )}
        </>
      )}
    </S.Section>
  )
}

export default Dashboard
